<script setup lang="ts">
  import { ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useReservationsPublicLink } from '@/composition/reservations/use-reservations-public-link';

  /** The finish screen's link card: share, copy, QR, a test booking and where to publish it. */
  const props = defineProps<{ branchId: number; disabled?: boolean }>();
  const sharingOpen = ref(false);
  const {
    url,
    downloading,
    copy,
    share,
    downloadQr,
    open,
  } = useReservationsPublicLink(() => props.branchId);
</script>

<template>
  <section class="reservations-guide-link-card">
    <h3 class="link-title">{{ $t('reservations.guide.finish.linkTitle') }}</h3>
    <label class="visually-hidden" for="reservations-guide-link">{{
      $t('reservations.guide.finish.linkTitle')
    }}</label>
    <PosUiInput id="reservations-guide-link" :value="url" readonly />
    <div class="link-actions">
      <PosUiButton
        class="share-button"
        icon="share-variant"
        :disabled="!url || disabled"
        @click="share"
        >{{ $t('reservations.guide.finish.share') }}</PosUiButton
      >
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="content-copy"
        :disabled="!url || disabled"
        @click="copy"
        >{{ $t('reservations.link.copy') }}</PosUiButton
      >
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="qrcode"
        :disabled="!url || downloading || disabled"
        :aria-busy="downloading"
        @click="downloadQr"
        >{{ $t('reservations.link.qr') }}</PosUiButton
      >
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="open-in-new"
        :disabled="!url || disabled"
        @click="open"
        >{{ $t('reservations.guide.finish.test') }}</PosUiButton
      >
    </div>
    <p class="fine-print">{{ $t('reservations.guide.finish.testHint') }}</p>
    <PosUiButton
      class="sharing-toggle"
      color="neutral"
      variant="ghost"
      :icon="sharingOpen ? 'chevron-up' : 'chevron-down'"
      :aria-expanded="sharingOpen ? 'true' : 'false'"
      aria-controls="reservations-guide-sharing"
      @click="sharingOpen = !sharingOpen"
      >{{ $t('reservations.link.whereToShare') }}</PosUiButton
    >
    <div
      v-show="sharingOpen"
      id="reservations-guide-sharing"
      class="sharing-instructions"
    >
      <section v-for="channel in ['gbp', 'ig']" :key="channel">
        <h4>{{ $t(`reservations.link.${channel}Title`) }}</h4>
        <ol>
          <li v-for="step in 3" :key="step">
            {{ $t(`reservations.link.${channel}Step${step}`) }}
          </li>
        </ol>
      </section>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .reservations-guide-link-card {
    @apply tw-flex tw-flex-col tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-p-4;
    p {
      @apply tw-m-0;
    }
    .link-title {
      @apply tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .pos-ui-input {
      @apply tw-w-full;
    }
    .link-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .fine-print {
      @apply tw-text-xs tw-leading-relaxed tw-text-neutral-500;
    }
    .sharing-instructions {
      @apply tw-grid tw-w-full tw-gap-5 tw-rounded-lg tw-bg-canvas tw-p-4 sm:tw-grid-cols-2;
      h4 {
        @apply tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900;
      }
      ol {
        @apply tw-mb-0 tw-mt-2 tw-list-decimal tw-space-y-2 tw-pl-5 tw-text-sm tw-leading-relaxed tw-text-neutral-600;
      }
    }
    .visually-hidden {
      @apply tw-sr-only;
    }
  }
</style>
